/**
 * useTilt — 3D perspective tilt effect on hover.
 * Returns a ref to attach to the element + style to apply.
 */

import { useRef, useState, useCallback } from "react";

interface TiltStyle {
  transform: string;
  transition: string;
}

export function useTilt(maxDeg = 3) {
  const ref = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<TiltStyle>({
    transform: "perspective(800px) rotateX(0deg) rotateY(0deg)",
    transition: "transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
  });

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateY = ((x - centerX) / centerX) * maxDeg;
    const rotateX = ((centerY - y) / centerY) * maxDeg;

    setStyle({
      transform: `perspective(800px) rotateX(${rotateX.toFixed(2)}deg) rotateY(${rotateY.toFixed(2)}deg) scale(1.02)`,
      transition: "transform 0.1s ease-out",
    });
  }, [maxDeg]);

  const handleMouseLeave = useCallback(() => {
    setStyle({
      transform: "perspective(800px) rotateX(0deg) rotateY(0deg) scale(1)",
      transition: "transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
    });
  }, []);

  return { ref, style, handleMouseMove, handleMouseLeave };
}
